import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    OnDestroy,
    OnInit,
    ViewChild
} from "@angular/core";
import {Router} from "@angular/router";
import {Api, ApiService} from "../../../../../../common/services/api.service";
import {ToastService} from "../../../../../../common/services/toast.service";
import {Base} from "../../../../../../common/interfaces/base.interfaces";
import {HelpersService} from "../../../../../../common/services/helpers.service";
import {Modal, ModalService} from "../../../../../services/modal.service";
import {Warehouse} from "../../../../../../common/interfaces/warehouse.interface";
import {FormControl} from "@angular/forms";
import {debounceTime, distinctUntilChanged, takeUntil} from "rxjs/operators";
import {CommonFormComponent} from "../../../../../../common/components/form";
import {SpinnerService} from "../../../../../../common/services/spinner.service";
import {Table} from "../../../../../../common/interfaces/table.interface";
import {Table2Component} from "../../../../../../common/components/table2";
import {Form} from "../../../../../../common/interfaces/form.interface";
import ISelectOption = Form.ISelectOption;
import {ShippyCarrierMappingFormComponent} from "../form/form.component";
import {AddressFormComponent} from "../../address";
import {Api3Service} from "../../../../../../common/services/api3.service";
import {ConfirmComponent} from "../../../../../../common/components/confirm/confirm.component";


@Component({
    selector: "section-shippy-carrier-mapping-list",
    templateUrl: "list.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShippyCarrierMappingListComponent implements Base.IComponent, OnInit, OnDestroy {

    private destroy$: EventEmitter<boolean> = new EventEmitter(false);

    public state: Base.IState;


    @ViewChild(Table2Component, {static: false})
    public tableRef: Table2Component;

    @ViewChild(ConfirmComponent, {static: false})
    public confirmRef: ConfirmComponent;

    /**
     * Table / list options (api url, columns data & title etc)
     * @type {Table.ISettings}
     */
    public listTable: Table.ISettings;

    public constructor(
        private toastService: ToastService,
        private modalService: ModalService,
        private changeDetectorRef: ChangeDetectorRef,
        private spinnerService: SpinnerService,
        private api3Service: Api3Service,
    ) {
    }

    private get url(): Table.ITableApi {
        return {
            url: [this.state.section, "shipping", "carriers-services-map"],
            query: {
                relations: [
                    "Hub:id,customers_inventory_name,customers_sub_inventory",
                    "CountryDetails:country_iso_2,country_name",
                    "Customer:id,name",
                    "Courier:id,display_name",
                    "CourierService:id,service_name",
                ],
            },
            version: 3
        };
    }


    /**
     * Prepare list/table
     * @returns {void}
     */
    private prepareList(): void {

        this.listTable = {
            api: this.url,
            actions: [
                {
                    name: "edit",
                    title: "Edit",
                    click: async (row: any): Promise<any> => {
                        await this.modalService.open(ShippyCarrierMappingFormComponent, {
                            data: row,
                            action: "edit",
                            state: this.state,
                        });

                        this.tableRef.reload();
                    }
                },
                {
                    name: "delete",
                    title: "Delete",
                    click: async (row: any): Promise<any> => {
                        this.delete(row);
                    }
                }
            ],
            columns: [
                {
                    "data": "hub.customers_inventory_name",
                    "name": "Hub.customers_inventory_name",
                    "title": "Outbound hub",
                    sortable: false,
                },
                {
                    "data": "hub.customers_sub_inventory",
                    "name": "Hub.customers_sub_inventory",
                    "title": "Sub inventory",
                    sortable: false,
                },
                {
                    "data": "country_details.country_name",
                    "name": "CountryDetails.country_name",
                    "title": "Destination country",
                    sortable: false
                },
                {
                    "data": "customer.name",
                    "name": "Customer.name",
                    "title": "Customer",
                    sortable: false
                },
                {
                    "data": "courier.display_name",
                    "name": "Courier.display_name",
                    "title": "Courier",
                    sortable: false
                },
                {
                    "data": "courier_service.service_name",
                    "name": "CourierService.service_name",
                    "title": "Courier Service",
                    sortable: false
                },
                {
                    "data": "shippy_pro_carrier_name",
                    "title": "Carrier",
                },
            ],
            infinity_scroll: true,
        };
        this.changeDetectorRef.markForCheck();
    }

    private async delete(row: any): Promise<any> {
        if (!await this.confirmRef.confirm("Are you sure want to delete this mapping?")) {
            return;
        }

        this.spinnerService.show();

        const {message, type}: Api.IResponse = await this.api3Service.delete(
            `${this.state.section}/shipping/carriers-services-map/${row.id}`);

        this.spinnerService.hide();

        if (type as string === "success") {
            this.toastService.show(message, "success");
            this.tableRef.reload();
        }
    }


    public async add(): Promise<any> {
        const res: Modal.IResponse = await this.modalService.open(ShippyCarrierMappingFormComponent, {
            state: this.state
        });

        if (res) {
            this.tableRef.reload();
        }
    }

    public ngOnInit(): void {
        this.prepareList();
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    public ngConfig(): Base.IConfig {
        return {
            name: "carrier-mapping-list",
            actions: {
                "browse": ["create_shippy_pro_labels"]
            }
        };
    }
}
